import React, { useState, useEffect } from "react";
import {
	  CheckIcon,
	  XMarkIcon,
	  ComputerDesktopIcon,
	  UserGroupIcon,
	  PaperClipIcon,
	  DocumentIcon,
	  PhotoIcon,
	  DocumentTextIcon,
	  ArchiveBoxIcon,
	  TrashIcon,
	  ArrowUpTrayIcon,
	  EyeIcon,
} from "@heroicons/react/24/outline";
import { SelectCompanies } from "./SelectCompanies";

// Límite de archivos individuales
const MAX_ARCHIVOS_INDIVIDUALES = 2;

const EditActaForm = ({ acta, onSubmit, onCancel, onDeleteArchivo }) => {
	const [formData, setFormData] = useState({
		empresa_nit: "",
		hora_inicio: "",
		hora_fin: "",
		tipo_actividad: "auditoria",
		tipo_proceso: "revisoria",
		modalidad: "presencial",
		descripcion: "",
		nombre_persona: "",
		observaciones: "",
	});
	  
	const [archivosNuevos, setArchivosNuevos] = useState([]);
	const [archivosExistentes, setArchivosExistentes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
	const [archivosAEliminar, setArchivosAEliminar] = useState([]);

	useEffect(() => {
	    if (acta) {
		// Convertir fechas al formato correcto (YYYY-MM-DDThh:mm)
		const formatDateTime = (dateString) => {
		    if (!dateString) return "";
		    const date = new Date(dateString);
		    const pad = (n) => n.toString().padStart(2, "0");
		    const year = date.getFullYear();
		    const month = pad(date.getMonth() + 1);
		    const day = pad(date.getDate());
		    const hours = pad(date.getHours());
		    const minutes = pad(date.getMinutes());
		    return `${year}-${month}-${day}T${hours}:${minutes}`;
		};

		setFormData({
		    empresa_nit: acta.empresa_nit || "",
		    hora_inicio: formatDateTime(acta.hora_inicio),
		    hora_fin: formatDateTime(acta.hora_fin),
		    tipo_actividad: acta.tipo_actividad || "auditoria",
		    tipo_proceso: acta.tipo_proceso || "revisoria",
		    modalidad: acta.modalidad || "presencial",
		    descripcion: acta.descripcion || "",
		    nombre_persona: acta.nombre_persona || "",
		    observaciones: acta.observaciones || "",
		});

                if (acta.archivos_adjuntos && acta.archivos_adjuntos.length > 0) {
			setArchivosExistentes(acta.archivos_adjuntos);
	        }

	        // Si hay empresa seleccionada, cargarla
		    if (acta.empresa_nit) {
		       setEmpresaSeleccionada({
		       nit: acta.empresa_nit,
		       nombre: acta.empresa_nombre || "Empresa",
		    });
		}
		if (acta.archivos_adjuntos && acta.archivos_adjuntos.length > 0) {
			console.log("Archivos existentes desde backend:", acta.archivos_adjuntos);
			setArchivosExistentes(acta.archivos_adjuntos);
		}
	     }
	}, [acta]);

        const handleChange = (e) => {
	    const { name, value } = e.target;
	    setFormData((prev) => ({
		 ...prev,
		 [name]: value,
	    }));
	};

	// Manejar seleccion de nuevos archivos
	const handleArchivosChange = (e) =>{
		const nuevosArchivos = Array.from(e.target.files);
		console.log("Archivos seleccionados:", nuevosArchivos.map(f => f.name));
		const archivosValidos = nuevosArchivos.filter((archivo) => {
			const maxSize = 10 * 1024 * 1024; // 10MB
			if (archivo.size > maxSize) {
				setError(`El archivo ${archivo.name} excede el capacidad total de 10MB`);
				return false;
			}
			return true;
		});

		if (archivosValidos.length > 0) {
			setArchivosNuevos((prev) => [...prev, ...archivosValidos]);
		}
		e.target.value = "";
	};

	const eliminarArchivoNuevo = (index) => {
		setArchivosNuevos(prev => prev.filter((_, i) => i !== index));
	};

	// Eliminar archivo nuevo de la lista
	const marcarArchivoParaEliminar = (archivoId) => {
		setArchivosAEliminar((prev) => [...prev, archivoId]);
		setArchivosExistentes((prev) => prev.filter((a) => a.id !== archivoId));
	};

	// Restaurar archivo eliminado
	const restaurarArchivo = (archivo) => {
		setArchivosAEliminar((prev) => prev.filter((id) => id !== archivo.id));
		setArchivosExistentes((prev) => [...prev, archivo]);
	};

	// Ver PDF
	const handleViewPDF = (archivo) => {
		if (archivo.url) {
			window.open(archivo.url, "_blank");
		}	    
	};

	const toggleModalidad = () => {
	      setFormData((prev) => ({
	            ...prev,
	            modalidad: prev.modalidad === "presencial" ? "virtual" : "presencial",
	      }));
	};

	const formatDateForDisplay = (dateTimeString) => {
		if (!dateTimeString) return "";
		const [datePart, timePart] = dateTimeString.split("T");
		const [year, month, day] = datePart.split("-");
		return `${day}/${month}/${year} ${timePart || ""}`;
	};

	const getMinDate = () => {
	      const date = new Date();
	      date.setDate(date.getDate() - 30);
	      return date.toISOString().slice(0, 10);
	};

	const getMaxDate = () => {
	      const date = new Date();
	      date.setDate(date.getDate() + 30);
	      return date.toISOString().slice(0, 10);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		// Validar fechas
		if (formData.hora_inicio && formData.hora_fin) {
			const inicio = new Date(formData.hora_inicio);
			const fin = new Date(formData.hora_fin);
			if (fin <= inicio) {
				setError("La hora de fin debe ser posterior a la hora de inicio");
			        return;
		        }
		}

		// Validar empresa
		if (!formData.empresa_nit) {
			setError("Debe seleccionar una empresa");
		        return;
		}
		
		// Validar archivos
		if (archivosNuevos.length > MAX_ARCHIVOS_INDIVIDUALES) {
			setError(
			       `Total ${MAX_ARCHIVOS_INDIVIDUALES} archivos individuales. Si necesita mas, almacene en ZIP, RAR o 7z.`
		        );
		        return;
		}
		
		setLoading(true);
		console.log("Estado antes de enviar:");
		console.log("archivosNuevos.length:", archivosNuevos.length);
		console.log("archivosAEliminar:", archivosAEliminar);
		console.log("archivosExistentes:", archivosExistentes.map(a => a.id));
		try {
			//const submitData = { ...formData };
			let dataToSubmit;
			const hayCambiosArchivos = archivosNuevos.length > 0 || archivosAEliminar.length > 0;
			
			if (hayCambiosArchivos) {
				const fd = new FormData();
				Object.keys(formData).forEach(key => {
					const value = formData[key];
					if (value !== null && value !== undefined && value !== '') {
						fd.append(key, value);
					}
				});
				archivosNuevos.forEach(archivo => {
					fd.append('archivos', archivo);
				});
				if (archivosAEliminar.length > 0) {
					fd.append('archivos_eliminar', JSON.stringify(archivosAEliminar));
				}
				console.log("Enviando FormData con archivos:");				
				await onSubmit(fd);
			} else {
				dataToSubmit = { ...formData };
				delete dataToSubmit.archivos_nuevos;
				delete dataToSubmit.archivos_eliminar;
				console.log("Enviando objeto JSON (sin archivos):", dataToSubmit);
				await onSubmit(dataToSubmit);
			}      
		} catch (err) {
			setError(err.message || "Error al actualizar el acta");
		} finally {
			setLoading(false);
		}
	};

	const handleChangeNit = (nit, empresa) => {
		setFormData((prev) => ({ ...prev, empresa_nit: nit }));
		setEmpresaSeleccionada(empresa);
	};

	//Obtener icono segun tipo de archivo
	const getIconoArchivo = (nombre) => {
		const extension = nombre.split(".").pop().toLowerCase();
		if (["jpg", "jpeg", "png", "gif", "bmp"].includes(extension)) {
			return <PhotoIcon className="h-5 w-5 text-blue-500" />;
		} else if (extension === "pdf") {
			return <DocumentTextIcon className="h-5 w-5 text-red-500" />;
		} else if (["doc", "docx"].includes(extension)) {
		        return <DocumentIcon className="h-5 w-5 text-blue-700" />;
		} else if (["xls", "xlsx"].includes(extension)) {
		        return <DocumentIcon className="h-5 w-5 text-green-600" />;
		} else if (["zip", "rar", "7z"].includes(extension)) {
		        return <ArchiveBoxIcon className="h-5 w-5 text-yellow-600" />;
		} else {
		       return <DocumentIcon className="h-5 w-5 text-gray-500" />;
		}
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};
	const tiposActividad = [
		{ value: "auditoria", label: "Auditoría", icon: "🔍" },
		{ value: "consultoria", label: "Consultoría", icon: "💼" },
		{ value: "asesoria", label: "Asesoría", icon: "📋" },
		{ value: "sagrilaft", label: "SAGRILAFT", icon: "🛡️" },
		{ value: "revisoria", label: "Revisoría", icon: "📝" },
		{ value: "capacitacion", label: "Capacitación", icon: "👨‍🏫" },
		{ value: "otro", label: "Otro", icon: "📌" },
	];

	const tiposProcesos = [
		{ value: "revisoria", label: "Revisoría", icon: "👁️" },
		{ value: "outsourcing", label: "Outsourcing", icon: "🤝" },
		{ value: "asesoria", label: "Asesoría", icon: "💡" },
		{ value: "auditoria", label: "Auditoría", icon: "🔍" },
		{ value: "sagrilaft", label: "SAGRILAFT", icon: "🛡️" },
		{ value: "comercial", label: "Comercial", icon: "💰" },
		{ value: "financiero", label: "Financiero", icon: "📊" },
	];

  return(
   <form onSubmit={handleSubmit} className="space-y-6">
     {/* Encabezado */}
     <div className="border-b border-gray-200 pb-4">
       <h3 className="text-lg font-semibold text-gray-900">
	 Editar Acta de Servicio
       </h3>
       <p className="text-sm text-gray-500 mt-1">
         Modifique los datos de la actividad realizada
       </p>
       {acta && (
         <p className="text-xs text-gray-400 mt-1">
	   Acta N°: {acta.numero_acta || acta.id}
	 </p>
       )}
     </div>
     {error && (
       <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-start">
         <XMarkIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
	 <span>{error}</span>
       </div>
     )}

     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
       {/* Columna izquierda */}
       <div className="space-y-5">
         {/* Empresa */}
	 <div>
	   <label className="block text-sm font-medium text-gray-700 mb-1">
	     Empresa
           </label>
	   <SelectCompanies
	     value={formData.empresa_nit}
	     onChange={handleChangeNit}
             placeholder="Seleccione una empresa"
	   />
	     {empresaSeleccionada && (
               <p className="mt-1 text-xs text-gray-500">
                 {empresaSeleccionada.nombre} - {empresaSeleccionada.nit}
               </p>
             )}
           </div>

	   {/* Tipo de Actividad */}
	   <div>
	     <label className="block text-sm font-medium text-gray-700 mb-1">
	       Tipo de Actividad *
	     </label>
             <select
	       name="tipo_actividad"
	       required
	       value={formData.tipo_actividad}
	       onChange={handleChange}
	       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	     >
	       {tiposActividad.map((tipo) => (
	         <option key={tipo.value} value={tipo.value}>
	           {tipo.icon} {tipo.label}
	         </option>
	       ))}
	     </select>
	   </div>

           {/* Tipo de Proceso */}
	   <div>
	     <label className="block text-sm font-medium text-gray-700 mb-1">
	       Tipo de Proceso *
	     </label>
	     <select
	       name="tipo_proceso"
	       required
	       value={formData.tipo_proceso}
	       onChange={handleChange}
	       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	     >
	       {tiposProcesos.map((tipo) => (
	         <option key={tipo.value} value={tipo.value}>
		   {tipo.icon} {tipo.label}
		 </option>
	       ))}
	     </select>
	   </div>

	   {/* Modalidad */}
	   <div className="pt-2">
	     <div className="flex items-center">
	       <button
	         type="button"
		 onClick={toggleModalidad}
		 className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
	         formData.modalidad === "presencial"
                   ? "bg-green-600"
                   : "bg-gray-200"
	         }`}
	       >
	         <span
		   className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
	             formData.modalidad === "presencial"
		       ? "translate-x-5"
	               : "translate-x-0"
		     }`}
	         />
	       </button>
	       <div className="ml-3 flex items-center">
                 {formData.modalidad === "presencial" ? (
                   <>
                     <UserGroupIcon className="h-5 w-5 text-green-600 mr-2" />
		     <span className="text-sm font-medium text-gray-900">
	               Presencial
		     </span>
	           </>
                 ) : (
	           <>
	             <ComputerDesktopIcon className="h-5 w-5 text-blue-600 mr-2" />
	             <span className="text-sm font-medium text-gray-900">
	               Virtual
		     </span>
	           </>
	         )}
               </div>
	     </div>
           </div>
		     
	   {/* Nombre Persona */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
               Nombre de la Persona que Realiza *
	     </label>
             <input
               type="text"
	       name="nombre_persona"
	       required
	       value={formData.nombre_persona}
	       onChange={handleChange}
	       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	       placeholder="Nombre completo"
	     />
           </div>
	 </div>

	 {/* Columna derecha */}
	 <div className="space-y-5">
	   {/* Hora de Inicio */}
	   <div>
	     <label className="block text-sm font-medium text-gray-700 mb-1">
	       Fecha y Hora de Inicio *
	     </label>
	     <div className="space-y-2">
	       <input
	         type="date"
	         value={formData.hora_inicio.split("T")[0] || ""}
	         onChange={(e) => {
		   const timePart = formData.hora_inicio.split("T")[1] || "09:00";
		   setFormData((prev) => ({
		     ...prev,
		     hora_inicio: `${e.target.value}T${timePart}`,
		   }));
		 }}
	         min={getMinDate()}
	         max={getMaxDate()}
	         className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	       />
	       <input
	         type="time"
	         value={formData.hora_inicio.split("T")[1] || ""}
	         onChange={(e) => {
	           const datePart = formData.hora_inicio.split("T")[0] || 
		   new Date().toISOString().split("T")[0];
	           setFormData((prev) => ({
		     ...prev,
		     hora_inicio: `${datePart}T${e.target.value}`,
		   }));
		 }}
	           className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	       />
	     </div>
	   </div>
           
	   {/* Hora de Fin */}
	   <div>
	     <label className="block text-sm font-medium text-gray-700 mb-1">
	       Fecha y Hora de Fin *
	     </label>
	     <div className="space-y-2">
	       <input
	         type="date"
	         value={formData.hora_fin.split("T")[0] || ""}
	         onChange={(e) => {
		   const timePart = formData.hora_fin.split("T")[1] || "17:00";
		   setFormData((prev) => ({
		     ...prev,
		     hora_fin: `${e.target.value}T${timePart}`,
		   }));
		 }}
	         min={formData.hora_inicio.split("T")[0] || getMinDate()}
	         max={getMaxDate()}
	         className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	       />
	       <input
	         type="time"
	         value={formData.hora_fin.split("T")[1] || ""}
	         onChange={(e) => {
		   const datePart = formData.hora_fin.split("T")[0] ||
		   new Date().toISOString().split("T")[0];
		   setFormData((prev) => ({
		     ...prev,
		     hora_fin: `${datePart}T${e.target.value}`,
		   }));
		 }}
	         className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	       />
	     </div>
	   </div>
	   
	   {/* Duración */}
	   {formData.hora_inicio && formData.hora_fin && (
	     <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
	       <div className="flex justify-between items-center">
	         <span className="text-sm font-medium text-gray-700">
		   Duración:
		 </span>
		 <span className="text-sm font-semibold text-blue-600">
		   {(() => {
		     const inicio = new Date(formData.hora_inicio);
		     const fin = new Date(formData.hora_fin);
		     const diffHours = (fin - inicio) / (1000 * 60 * 60);
		     return `${diffHours.toFixed(2)} horas`;
		   })()}
		 </span>
	       </div>
	     </div>		              )}
	   </div>
	 </div>

	 {/* Archivos Adjuntos - Versión para edición */}
	 <div className="border-t border-gray-200 pt-5">
	   <div className="flex items-center mb-3">
	     <PaperClipIcon className="h-5 w-5 text-gray-500 mr-2" />
	     <h4 className="text-md font-medium text-gray-900">Archivos Adjuntos</h4>
	     <span className="ml-2 text-xs text-gray-500">
	       (Máximo {MAX_ARCHIVOS_INDIVIDUALES} archivos nuevos)
	     </span>
	   </div>
           
           <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
	     {/* Archivos existentes */}
             {archivosExistentes.length > 0 && (
               <div>
	         <p className="text-xs font-medium text-gray-700 mb-2">
		   Archivos actuales:
		 </p>
		 <div className="space-y-2">
		   {archivosExistentes.map((archivo) => (
		     <div
		       key={archivo.id}
		       className="flex items-center justify-between bg-white p-2 rounded border border-gray-200"
		     >
		       <div className="flex items-center space-x-3">
		         {getIconoArchivo(archivo.nombre_archivo || archivo.nombre)}
			 <div>
			   <p className="text-sm font-medium text-gray-700 truncate max-w-xs">
			     {archivo.nombre_archivo || archivo.nombre}
			   </p>
			   {archivo.tamaño && (
			     <p className="text-xs text-gray-500">
			       {formatFileSize(archivo.tamaño)}
			     </p>
			   )}
			 </div>
		       </div>
		       <div className="flex items-center space-x-2">
		         <button
			   type="button"
			   onClick={() => handleViewPDF(archivo)}
			   className="text-blue-500 hover:text-blue-700 p-1"
			   title="Ver archivo"
			 >
			   <EyeIcon className="h-4 w-4" />
			 </button>
			 <button
			   type="button"
			   onClick={() => marcarArchivoParaEliminar(archivo.id)}
			   className="text-red-500 hover:text-red-700 p-1"
			   title="Eliminar archivo"
			 >
			   <TrashIcon className="h-4 w-4" />
			 </button>
		       </div>
		     </div>
		   ))}
		 </div>
	       </div>
	     )}
	     
	     {/* Archivos nuevos a subir */}
	     {archivosNuevos.length > 0 && (
	       <div>
	         <p className="text-xs font-medium text-green-700 mb-2">
		   Nuevos archivos a subir:
		 </p>
		 <div className="space-y-2">
		   {archivosNuevos.map((archivo, index) => (
		     <div
		       key={index}
		       className="flex items-center justify-between bg-green-50 p-2 rounded border border-green-200"
		     >
		       <div className="flex items-center space-x-3">
		         {getIconoArchivo(archivo.name)}
		       	 <div>
			   <p className="text-sm font-medium text-gray-700 truncate max-w-xs">
			     {archivo.name}
			   </p>
			   <p className="text-xs text-gray-500">
			     {formatFileSize(archivo.size)}
			   </p>
			 </div>
		       </div>
		       <button
		         type="button"
			 onClick={() => eliminarArchivoNuevo(index)}
			 className="text-red-500 hover:text-red-700 p-1"
		       >
			 <TrashIcon className="h-4 w-4" />
		       </button>
		     </div>
		   ))}
		 </div>
	       </div>
	     )}
	     
	     {/* Selector de archivos */}
	     <div className="flex items-center justify-center w-full">
	       <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50">
	         <div className="flex flex-col items-center justify-center pt-5 pb-6">
	           <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mb-2" />
	           <p className="mb-2 text-sm text-gray-500">
	             <span className="font-semibold">Click para seleccionar</span>{" "}
	             o arrastra archivos
	           </p>
	           <p className="text-xs text-gray-500">
	             PDF, Word, Excel, Imágenes, ZIP, RAR, 7z (Max 10MB)
	           </p>
	         </div>
	         <input
	           type="file"
	           className="hidden"
	           multiple
	           onChange={handleArchivosChange}
	           disabled={archivosNuevos.length >= MAX_ARCHIVOS_INDIVIDUALES}
	           accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar,.7z"
	         />
	       </label>
	     </div>
             
	     {/* Mensaje informativo */}
	     <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
	       <p>ℹ️ Los archivos marcados para eliminar se borrarán al guardar los cambios.</p>
	     </div>
	   </div>
	 </div>
         
	 {/* Descripción y Observaciones */}
         <div className="space-y-5">
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
	       Descripción de la Actividad *
	     </label>
	     <textarea
	       name="descripcion"
	       required
	       rows={4}
	       value={formData.descripcion}
	       onChange={handleChange}
	       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	       placeholder="Describa detalladamente la actividad realizada..."
	     />
	   </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">
	       Observaciones Adicionales
	     </label>
	     <textarea
	       name="observaciones"
	       rows={3}
	       value={formData.observaciones}
	       onChange={handleChange}
	       className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
	       placeholder="Agregue cualquier observación relevante..."
	     />
	   </div>
	 </div>

         {/* Resumen */}
         <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
	   <div className="flex items-center mb-2">
	     <CheckIcon className="h-5 w-5 text-blue-600 mr-2" />
	     <h4 className="text-sm font-semibold text-blue-800">
	       Resumen de Cambios
	     </h4>
	   </div>
	   <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
	     <div>
	       <span className="text-gray-600">Archivos nuevos:</span>
	       <span className="ml-2 font-medium">{archivosNuevos.length}</span>
	     </div>
	     <div>
	       <span className="text-gray-600">Archivos a eliminar:</span>
	       <span className="ml-2 font-medium text-red-600">
	         {archivosAEliminar.length}
	       </span>
	     </div>
	     <div>
	       <span className="text-gray-600">Archivos finales:</span>
	       <span className="ml-2 font-medium">
	         {archivosExistentes.length + archivosNuevos.length}
	       </span>
	     </div>
	   </div>
	 </div>
         
         {/* Botones */}
         <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 pt-4 border-t border-gray-200">
	   <button
	     type="button"
	     onClick={onCancel}
	     disabled={loading}
	     className="mt-3 sm:mt-0 w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
	   >
	     Cancelar
	   </button>
	   <button
	     type="submit"
	     disabled={loading}
	     className="w-full sm:w-auto px-4 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center"
	   >
	     {loading ? (
	       <>
	         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
		   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
		   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
		 </svg>
		 Guardando...
	       </>
	     ) : (
	       <>
	         <CheckIcon className="h-4 w-4 mr-2" />
	       	 Actualizar Acta
	       </>
	     )}
	   </button>
	 </div>
       </form>
     );
};
export default EditActaForm;

		               
